// Tax invoice and delivery challan PDFs. They follow the QUOTATION layout exactly (same letterhead, "Bill To", "Subject:" line,
// table style, totals box, "Total In Words", Notes, Terms & Conditions, and the "Yours faithfully / For <company> / seal /
// Authorized Signature" block), so all three documents look like one set.
// Dependencies are passed in (the app owns the logos, seals and company details), which also lets the module be rendered from a
// plain node script for testing.
//
// deps: { jsPDF, autoTable, COMPANIES, COMPANY_LOGOS, COMPANY_SEALS, numberToWordsINR, gstTotals, stateFromGstin }

const fmtDate = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB').replace(/\//g, '-') : '');

// Place of supply as the quotation prints it: "Karnataka (29)"
const STATE_CODES = { 'Jammu & Kashmir': '01', 'Himachal Pradesh': '02', Punjab: '03', Chandigarh: '04', Uttarakhand: '05', Haryana: '06', Delhi: '07', Rajasthan: '08', 'Uttar Pradesh': '09', Bihar: '10', Sikkim: '11', 'Arunachal Pradesh': '12', Nagaland: '13', Manipur: '14', Mizoram: '15', Tripura: '16', Meghalaya: '17', Assam: '18', 'West Bengal': '19', Jharkhand: '20', Odisha: '21', Chhattisgarh: '22', 'Madhya Pradesh': '23', Gujarat: '24', 'Dadra & Nagar Haveli and Daman & Diu': '26', Maharashtra: '27', Karnataka: '29', Goa: '30', Lakshadweep: '31', Kerala: '32', 'Tamil Nadu': '33', Puducherry: '34', 'Andaman & Nicobar Islands': '35', Telangana: '36', 'Andhra Pradesh': '37', Ladakh: '38' };
const placeOfSupply = (state) => (STATE_CODES[state] ? `${state} (${STATE_CODES[state]})` : state);

// Letterhead + right-hand block, identical to the quotation (title is 20pt bold, then "# number", date, place of supply).
const letterhead = (doc, company, deps, title, numberText, rightLines) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 15;
  const logo = deps.COMPANY_LOGOS[company.id];
  if (logo) {
    const logoH = 12;
    doc.addImage(logo, 'PNG', 14, 8, logoH * (900 / 184), logoH);
    y = 26;
  } else {
    doc.setFontSize(16); doc.setFont('helvetica', 'bold'); doc.setTextColor(37, 99, 235);
    doc.text(company.name, 14, y); doc.setTextColor(0, 0, 0); y += 6;
  }
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(company.address, 14, y); y += 4.5;
  doc.text(`Ph: ${company.phone}  |  Email: ${company.email}`, 14, y); y += 4.5;
  if (company.whatsapp) { doc.text(`WhatsApp: ${company.whatsapp}`, 14, y); y += 4.5; }
  doc.text(`GSTIN: ${company.gstin}${company.msme ? `  |  MSME/Udyam No: ${company.msme}` : ''}`, 14, y);

  doc.setFontSize(20); doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth - 14, 20, { align: 'right' });
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(numberText, pageWidth - 14, 27, { align: 'right' });
  let ry = 32;
  rightLines.filter(Boolean).forEach(l => { doc.text(l, pageWidth - 14, ry, { align: 'right' }); ry += 5; });

  y = Math.max(y + 8, ry - 3);
  doc.setDrawColor(200, 200, 200); doc.line(14, y, pageWidth - 14, y);
  return y + 7;
};

// "Bill To:" block, same as the quotation
const partyBlock = (doc, y, heading, name, address, gstin) => {
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(heading, 14, y); y += 5;
  doc.setFont('helvetica', 'bold'); doc.text(name || '', 14, y); y += 5;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  if (address) doc.splitTextToSize(address, 100).forEach(line => { doc.text(line, 14, y); y += 4.5; });
  if (gstin) { doc.text(`GSTIN: ${gstin}`, 14, y); y += 4.5; }
  return y + 3;
};

const subjectLine = (doc, y, text) => {
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.splitTextToSize(`Subject: ${text}`, doc.internal.pageSize.getWidth() - 28).forEach(l => { doc.text(l, 14, y); y += 4.5; });
  return y + 1.5;
};

// The quotation's closing block, verbatim: "Yours faithfully," / "For <company>" / seal / "Authorized Signature".
const signature = (doc, company, deps, finalY, pageHeight, pageWidth) => {
  const seal = deps.COMPANY_SEALS[company.id];
  const sigBlockHeight = seal ? 45 : 35;
  if (finalY + 15 + sigBlockHeight > pageHeight) { doc.addPage(); finalY = 20; } else { finalY = Math.max(finalY + 15, pageHeight - sigBlockHeight); }
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('Yours faithfully,', pageWidth - 14, finalY, { align: 'right' });
  doc.text(`For ${company.name}`, pageWidth - 14, finalY + 5, { align: 'right' });
  if (seal) doc.addImage(seal, 'PNG', pageWidth - 14 - 20, finalY + 8, 20, 20);
  doc.setFont('helvetica', 'bold');
  doc.text('Authorized Signature', pageWidth - 14, finalY + (seal ? 32 : 20), { align: 'right' });
  return finalY;
};

// Notes / bank details / terms: heading in bold 9pt, body 9pt (terms 8pt), paginating like the quotation does.
const makeFlow = (doc, startY, pageHeight, pageWidth) => {
  let f = startY;
  const ensure = (h = 4) => { if (f + h > pageHeight - 20) { doc.addPage(); f = 20; } };
  const para = (heading, text, size) => {
    if (!text) return;
    ensure(9); doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(heading, 14, f); f += 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(size);
    String(text).split('\n').filter(Boolean).forEach(p => doc.splitTextToSize(p, pageWidth - 28).forEach(l => { ensure(); doc.text(l, 14, f); f += size > 8 ? 4.5 : 4; }));
    f += 3;
  };
  return { para, y: () => f, ensure };
};

export const buildInvoicePDFDoc = (inv, deps) => {
  const { jsPDF, autoTable } = deps;
  const company = deps.COMPANIES[inv.companyId] || deps.COMPANIES.rb_infotech;
  const ownState = company.state || 'Karnataka';
  const partyState = deps.stateFromGstin(inv.clientGSTIN) || ownState;
  const items = (inv.items || []).map(i => ({ ...i, amount: (parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0) }));
  const net = items.reduce((s, i) => s + i.amount, 0);
  const t = deps.gstTotals(net, inv.gstRate, partyState, ownState);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = letterhead(doc, company, deps, inv.isProforma ? 'PROFORMA INVOICE' : 'TAX INVOICE', `# ${inv.invoiceNumber}`,
    [`Date: ${fmtDate(inv.date)}`, inv.dueDate ? `${inv.isProforma ? 'Valid Until' : 'Due Date'}: ${fmtDate(inv.dueDate)}` : '', `Place of Supply: ${placeOfSupply(partyState)}`]);
  y = partyBlock(doc, y, 'Bill To:', inv.clientName, inv.clientAddress, inv.clientGSTIN);
  const first = (items[0] && items[0].description) ? String(items[0].description).split(/ - |\n/)[0].slice(0, 70) : 'Goods and Services';
  y = subjectLine(doc, y, inv.subject || `${inv.isProforma ? 'Proforma Invoice' : 'Invoice'} for ${first}`);

  const half = t.rate / 2;
  const head = t.interstate
    ? [['#', 'Item & Description', 'HSN/SAC', 'Qty', 'Rate', 'IGST%', 'IGST Amt', 'Amount']]
    : [['#', 'Item & Description', 'HSN/SAC', 'Qty', 'Rate', 'CGST%', 'CGST Amt', 'SGST%', 'SGST Amt', 'Amount']];
  const rows = items.map((i, idx) => {
    const base = [idx + 1, i.description, i.hsn || '998713', i.qty, (parseFloat(i.rate) || 0).toFixed(2)];
    return t.interstate
      ? [...base, `${t.rate}%`, (i.amount * t.rate / 100).toFixed(2), i.amount.toFixed(2)]
      : [...base, `${half}%`, (i.amount * half / 100).toFixed(2), `${half}%`, (i.amount * half / 100).toFixed(2), i.amount.toFixed(2)];
  });
  autoTable(doc, {
    startY: y, head, body: rows, theme: 'grid',
    headStyles: { fillColor: [37, 99, 235], fontSize: 8 }, styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 45 } }
  });

  let f = doc.lastAutoTable.finalY + 6;
  const tx = pageWidth - 70;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  const line = (label, val) => { doc.text(label, tx, f); doc.text(val, pageWidth - 14, f, { align: 'right' }); f += 5; };
  line('Sub Total', t.net.toFixed(2));
  if (t.interstate) line(`IGST (${t.rate}%)`, t.igst.toFixed(2));
  else { line(`CGST (${half}%)`, t.cgst.toFixed(2)); line(`SGST (${half}%)`, t.sgst.toFixed(2)); }
  f += 1; doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text('Total', tx, f); doc.text(`Rs. ${t.total.toFixed(2)}`, pageWidth - 14, f, { align: 'right' });
  f += 10;
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.text('Total In Words:', 14, f);
  doc.setFont('helvetica', 'italic'); doc.text(`Indian Rupee ${deps.numberToWordsINR(t.total)}`, 14, f + 5);
  f += 14;

  const flow = makeFlow(doc, f, pageHeight, pageWidth);
  flow.para('Notes', inv.notes, 9);
  flow.para('Bank Details', inv.bankDetails, 9);
  flow.para('Terms & Conditions', inv.terms, 8);
  if (inv.isProforma) flow.para('Note', 'This is a proforma invoice and not a tax invoice. A tax invoice will be issued on supply / confirmation.', 8);
  signature(doc, company, deps, flow.y(), pageHeight, pageWidth);
  return doc;
};

export const buildChallanPDFDoc = (ch, deps) => {
  const { jsPDF, autoTable } = deps;
  const company = deps.COMPANIES[ch.companyId] || deps.COMPANIES.rb_infotech;
  const partyState = deps.stateFromGstin(ch.clientGSTIN) || company.state || 'Karnataka';
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = letterhead(doc, company, deps, 'DELIVERY CHALLAN', `# ${ch.challanNumber}`,
    [`Date: ${fmtDate(ch.date)}`, ch.vehicleNo ? `Vehicle No: ${ch.vehicleNo}` : '', `Place of Supply: ${placeOfSupply(partyState)}`]);
  y = partyBlock(doc, y, 'Deliver To:', ch.clientName, ch.deliveryAddress || ch.clientAddress, ch.clientGSTIN);
  const refs = [ch.quotationNumber ? `quotation ${ch.quotationNumber}` : '', ch.invoiceNumber ? `invoice ${ch.invoiceNumber}` : ''].filter(Boolean).join(', ');
  y = subjectLine(doc, y, ch.subject || `Delivery Challan - ${ch.purpose || 'Supply of goods'}${refs ? ` (against ${refs})` : ''}`);
  if (ch.transporter) { doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.text(`Driver / Transporter: ${ch.transporter}`, 14, y); y += 6; }

  const items = ch.items || [];
  autoTable(doc, {
    startY: y,
    head: [['#', 'Item & Description', 'Serial / Asset No.', 'Qty', 'Unit']],
    body: items.map((i, idx) => [idx + 1, i.description, i.serialNo || '', i.qty, i.unit || 'Nos']),
    theme: 'grid', headStyles: { fillColor: [37, 99, 235], fontSize: 8 }, styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: { 0: { cellWidth: 8 }, 3: { cellWidth: 16 }, 4: { cellWidth: 16 } }
  });
  let f = doc.lastAutoTable.finalY + 6;
  const totalQty = items.reduce((s, i) => s + (parseFloat(i.qty) || 0), 0);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text(`Total quantity: ${totalQty}`, pageWidth - 14, f, { align: 'right' }); f += 10;

  const flow = makeFlow(doc, f, pageHeight, pageWidth);
  flow.para('Notes', ch.notes, 9);
  flow.para('Terms & Conditions', ch.terms, 8);

  // The quotation's signature block on the right, plus a "received by" block on the left at the same height
  const seal = deps.COMPANY_SEALS[company.id];
  const sigBlockHeight = seal ? 45 : 35;
  let by = flow.y();
  if (by + 15 + sigBlockHeight > pageHeight) { doc.addPage(); by = 20; } else { by = Math.max(by + 15, pageHeight - sigBlockHeight); }
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('Received the above goods in good condition', 14, by);
  doc.text('Name: ______________________', 14, by + 9);
  doc.text('Signature & Stamp: ______________', 14, by + 17);
  doc.text('Date: ______________________', 14, by + 25);
  doc.text('Yours faithfully,', pageWidth - 14, by, { align: 'right' });
  doc.text(`For ${company.name}`, pageWidth - 14, by + 5, { align: 'right' });
  if (seal) doc.addImage(seal, 'PNG', pageWidth - 14 - 20, by + 8, 20, 20);
  doc.setFont('helvetica', 'bold');
  doc.text('Authorized Signature', pageWidth - 14, by + (seal ? 32 : 20), { align: 'right' });
  return doc;
};

// ---------------------------------------------------------------------------------------------------------------------
// Tally Prime's standard "Tax Invoice" print layout (boxed header with company / consignee / buyer and the reference grid,
// item table with the tax rows under it, "Amount Chargeable (in words)", HSN/SAC tax summary, "Tax Amount (in words)",
// PAN, declaration, bank details, "for <company> Authorised Signatory", "This is a Computer Generated Invoice").
// ---------------------------------------------------------------------------------------------------------------------
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const tallyDateText = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${parseInt(m[3], 10)}-${MONTHS[parseInt(m[2], 10) - 1]}-${m[1].slice(2)}` : '';
};
const stateNameCode = (state) => (STATE_CODES[state] ? `${state}, Code : ${STATE_CODES[state]}` : state || '');
const panFromGstin = (g) => (/^\d{2}[A-Z]{5}\d{4}[A-Z]/.test(String(g || '').toUpperCase()) ? String(g).toUpperCase().slice(2, 12) : '');
const money2 = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const DEFAULT_DECLARATION = 'We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.';

export const buildTallyInvoicePDFDoc = (inv, deps) => {
  const { jsPDF, autoTable } = deps;
  const company = deps.COMPANIES[inv.companyId] || deps.COMPANIES.rb_infotech;
  const ownState = company.state || 'Karnataka';
  const partyState = deps.stateFromGstin(inv.clientGSTIN) || ownState;
  const items = (inv.items || []).map(i => ({ ...i, qtyN: parseFloat(i.qty) || 0, rateN: parseFloat(i.rate) || 0 })).map(i => ({ ...i, amount: i.qtyN * i.rateN }));
  const net = items.reduce((s, i) => s + i.amount, 0);
  const t = deps.gstTotals(net, inv.gstRate, partyState, ownState);
  const half = t.rate / 2;
  const totalQty = items.reduce((s, i) => s + i.qtyN, 0);

  const doc = new jsPDF();
  const W = doc.internal.pageSize.getWidth();   // 210
  const H = doc.internal.pageSize.getHeight();  // 297
  const L = 10, R = W - 10, CW = R - L;
  doc.setLineWidth(0.25); doc.setDrawColor(0); doc.setTextColor(0);

  // ---- title
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text(inv.isProforma ? 'Proforma Invoice' : 'Tax Invoice', W / 2, 11, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.text('(ORIGINAL FOR RECIPIENT)', R, 11, { align: 'right' });

  // ---- header grid: left = company / consignee / buyer, right = references
  const top = 14, midX = L + 95;
  const hCo = 28, hCons = 26, hBuy = 31, hTotal = hCo + hCons + hBuy;
  doc.rect(L, top, CW, hTotal);
  doc.line(midX, top, midX, top + hTotal);
  doc.line(L, top + hCo, midX, top + hCo);
  doc.line(L, top + hCo + hCons, midX, top + hCo + hCons);

  const block = (x, y, w, lines) => {
    let yy = y + 4;
    lines.forEach(([txt, bold, size]) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(size || 7.5);
      doc.splitTextToSize(String(txt || ''), w - 4).forEach(l => { doc.text(l, x + 2, yy); yy += (size || 7.5) * 0.42 + 0.9; });
    });
  };
  block(L, top, 95, [
    [company.name, true, 9], [company.address, false, 7.5],
    [`GSTIN/UIN: ${company.gstin}`, false, 7.5], [`State Name : ${stateNameCode(ownState)}`, false, 7.5], [`E-Mail : ${company.email}`, false, 7.5]
  ]);
  block(L, top + hCo, 95, [
    ['Consignee (Ship to)', false, 7], [inv.shipToName || inv.clientName, true, 8.5], [inv.shipToAddress || inv.clientAddress, false, 7.5],
    [inv.clientGSTIN ? `GSTIN/UIN : ${inv.clientGSTIN}` : '', false, 7.5], [`State Name : ${stateNameCode(partyState)}`, false, 7.5]
  ]);
  block(L, top + hCo + hCons, 95, [
    ['Buyer (Bill to)', false, 7], [inv.clientName, true, 8.5], [inv.clientAddress, false, 7.5],
    [inv.clientGSTIN ? `GSTIN/UIN : ${inv.clientGSTIN}` : '', false, 7.5], [`State Name : ${stateNameCode(partyState)}`, false, 7.5], [`Place of Supply : ${stateNameCode(partyState)}`, false, 7.5]
  ]);

  // right-hand reference grid: 6 rows of two cells + a full-width "Terms of Delivery" cell
  const rowH = 10.2, cellW = (R - midX) / 2;
  const cells = [
    [['Invoice No.', inv.invoiceNumber, true], ['Dated', tallyDateText(inv.date), true]],
    [['Delivery Note', inv.deliveryNote], [inv.isProforma ? 'Valid Until' : 'Mode/Terms of Payment', inv.isProforma ? tallyDateText(inv.dueDate) : (inv.modeOfPayment || (inv.dueDate ? `Due ${tallyDateText(inv.dueDate)}` : ''))]],
    [['Reference No. & Date.', inv.reference], ['Other References', inv.otherReferences]],
    [["Buyer's Order No.", inv.buyerOrderNo], ['Dated', tallyDateText(inv.buyerOrderDate)]],
    [['Dispatch Doc No.', inv.dispatchDocNo], ['Delivery Note Date', tallyDateText(inv.deliveryNoteDate)]],
    [['Dispatched through', inv.dispatchedThrough], ['Destination', inv.destination]]
  ];
  cells.forEach((row, ri) => {
    const y0 = top + ri * rowH;
    if (ri > 0) doc.line(midX, y0, R, y0);
    doc.line(midX + cellW, y0, midX + cellW, y0 + rowH);
    row.forEach(([label, value, bold], ci) => {
      const x0 = midX + ci * cellW;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.text(label, x0 + 1.5, y0 + 3.5);
      doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(8);
      doc.splitTextToSize(String(value || ''), cellW - 3).slice(0, 2).forEach((l, li) => doc.text(l, x0 + 1.5, y0 + 7.2 + li * 3.2));
    });
  });
  const tdY = top + 6 * rowH; doc.line(midX, tdY, R, tdY);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.text('Terms of Delivery', midX + 1.5, tdY + 3.5);
  doc.setFontSize(7.5); doc.splitTextToSize(String(inv.termsOfDelivery || ''), R - midX - 3).slice(0, 5).forEach((l, i) => doc.text(l, midX + 1.5, tdY + 7 + i * 3.4));

  // ---- item table with the tax rows underneath
  const body = items.map((i, idx) => [idx + 1, { content: i.description, styles: { fontStyle: 'bold' } }, i.hsn || '', `${i.qtyN} ${i.unit || 'Nos'}`, money2(i.rateN), i.unit || 'Nos', money2(i.amount)]);
  const taxRow = (label, rate, amt) => [{ content: '', styles: {} }, { content: label, styles: { halign: 'right', fontStyle: 'bolditalic' } }, '', '', rate, '%', money2(amt)];
  if (t.interstate) body.push(taxRow('IGST', t.rate, t.igst));
  else { body.push(taxRow('CGST', half, t.cgst)); body.push(taxRow('SGST', half, t.sgst)); }
  autoTable(doc, {
    startY: top + hTotal,
    margin: { left: L, right: W - R },
    head: [['Sl No.', 'Description of Goods and Services', 'HSN/SAC', 'Quantity', 'Rate', 'per', 'Amount']],
    body,
    foot: [['', { content: 'Total', styles: { halign: 'right', fontStyle: 'bold' } }, '', { content: `${totalQty} ${(items[0] && items[0].unit) || 'Nos'}`, styles: { fontStyle: 'bold' } }, '', '', { content: `Rs. ${money2(t.total)}`, styles: { fontStyle: 'bold', halign: 'right' } }]],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 1.6, lineColor: [0, 0, 0], lineWidth: 0.25, textColor: [0, 0, 0] },
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
    footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
    columnStyles: { 0: { cellWidth: 11, halign: 'center' }, 1: { cellWidth: 78 }, 2: { cellWidth: 20, halign: 'center' }, 3: { cellWidth: 22, halign: 'right' }, 4: { cellWidth: 22, halign: 'right' }, 5: { cellWidth: 11, halign: 'center' }, 6: { cellWidth: 26, halign: 'right' } }
  });
  let y = doc.lastAutoTable.finalY;
  const ensure = (h) => { if (y + h > H - 12) { doc.addPage(); y = 14; } };

  // ---- amount chargeable in words
  ensure(14);
  doc.rect(L, y, CW, 12);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.text('Amount Chargeable (in words)', L + 1.5, y + 3.5); doc.text('E. & O.E', R - 1.5, y + 3.5, { align: 'right' });
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(`INR ${deps.numberToWordsINR(t.total)}`, L + 1.5, y + 8.5);
  y += 12;

  // ---- HSN/SAC tax summary
  const byHsn = new Map();
  items.forEach(i => { const k = i.hsn || '-'; byHsn.set(k, (byHsn.get(k) || 0) + i.amount); });
  const sumRows = [...byHsn.entries()].map(([hsn, taxable]) => t.interstate
    ? [hsn, money2(taxable), `${t.rate}%`, money2(taxable * t.rate / 100), money2(taxable * t.rate / 100)]
    : [hsn, money2(taxable), `${half}%`, money2(taxable * half / 100), `${half}%`, money2(taxable * half / 100), money2(taxable * t.rate / 100)]);
  const sumHead = t.interstate
    ? [[{ content: 'HSN/SAC', rowSpan: 2 }, { content: 'Taxable Value', rowSpan: 2 }, { content: 'Integrated Tax', colSpan: 2 }, { content: 'Total Tax Amount', rowSpan: 2 }], ['Rate', 'Amount']]
    : [[{ content: 'HSN/SAC', rowSpan: 2 }, { content: 'Taxable Value', rowSpan: 2 }, { content: 'Central Tax', colSpan: 2 }, { content: 'State Tax', colSpan: 2 }, { content: 'Total Tax Amount', rowSpan: 2 }], ['Rate', 'Amount', 'Rate', 'Amount']];
  const sumFoot = t.interstate
    ? [['Total', money2(t.net), '', money2(t.igst), money2(t.igst)]]
    : [['Total', money2(t.net), '', money2(t.cgst), '', money2(t.sgst), money2(t.cgst + t.sgst)]];
  ensure(30);
  autoTable(doc, {
    startY: y, margin: { left: L, right: W - R }, head: sumHead, body: sumRows, foot: sumFoot, theme: 'grid',
    styles: { fontSize: 7.5, cellPadding: 1.3, lineColor: [0, 0, 0], lineWidth: 0.25, textColor: [0, 0, 0], halign: 'right' },
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
    footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
    columnStyles: { 0: { halign: 'left' } }
  });
  y = doc.lastAutoTable.finalY;

  ensure(10);
  doc.rect(L, y, CW, 8);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.text('Tax Amount (in words) :', L + 1.5, y + 5);
  doc.setFont('helvetica', 'bold'); doc.text(`INR ${deps.numberToWordsINR(t.cgst + t.sgst + t.igst)}`, L + 36, y + 5);
  y += 8;

  // ---- PAN, declaration, bank details (left) and signature (right)
  const decl = inv.declaration || DEFAULT_DECLARATION;
  const bank = String(inv.bankDetails || '').split('\n').filter(Boolean);
  const leftLines = [];
  const pan = panFromGstin(company.gstin);
  if (pan) leftLines.push([`Company's PAN : ${pan}`, true]);
  leftLines.push(['Declaration', true], [decl, false]);
  const leftH = 8 + leftLines.length * 5 + (bank.length ? bank.length * 4 + 7 : 0) + 14;
  const boxH = Math.max(leftH, 44);
  ensure(boxH);
  doc.rect(L, y, CW, boxH); doc.line(midX + 5, y, midX + 5, y + boxH);
  let yy = y + 4;
  const writeLeft = (txt, bold, size = 7.5, gap = 3.6) => { doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(size); doc.splitTextToSize(String(txt), midX + 5 - L - 4).forEach(l => { doc.text(l, L + 2, yy); yy += gap; }); };
  if (pan) writeLeft(`Company's PAN : ${pan}`, true);
  yy += 1; writeLeft('Declaration', true); writeLeft(decl, false);
  if (bank.length) { yy += 2; writeLeft("Company's Bank Details", true); bank.forEach(b => writeLeft(b, false)); }
  const sx = midX + 5, sw = R - sx;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.text(`for ${company.name}`, R - 2, y + 5, { align: 'right' });
  const seal = deps.COMPANY_SEALS[company.id];
  if (seal) doc.addImage(seal, 'PNG', sx + sw / 2 - 10, y + 9, 20, 20);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.text('Authorised Signatory', R - 2, y + boxH - 3, { align: 'right' });
  y += boxH;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
  doc.text(inv.isProforma ? 'This is a Proforma Invoice and not a Tax Invoice. This is a Computer Generated Document.' : 'This is a Computer Generated Invoice', W / 2, Math.min(y + 5, H - 6), { align: 'center' });
  return doc;
};

// ---------------------------------------------------------------------------------------------------------------------
// Purchase order: same letterhead / table / totals / signature as the quotation set, addressed to the vendor.
// ---------------------------------------------------------------------------------------------------------------------
export const buildPurchaseOrderPDFDoc = (po, deps) => {
  const { jsPDF, autoTable } = deps;
  const company = deps.COMPANIES[po.companyId] || deps.COMPANIES.rb_infotech;
  const ownState = company.state || 'Karnataka';
  const vendorState = deps.stateFromGstin(po.vendorGSTIN) || ownState;
  const items = (po.items || []).map(i => ({ ...i, amount: (parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0) }));
  const net = items.reduce((s, i) => s + i.amount, 0);
  const t = deps.gstTotals(net, po.gstRate === '' || po.gstRate === undefined ? 18 : po.gstRate, vendorState, ownState);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = letterhead(doc, company, deps, 'PURCHASE ORDER', `# ${po.poNumber}`,
    [`Date: ${fmtDate(po.date)}`, po.deliveryDate ? `Delivery By: ${fmtDate(po.deliveryDate)}` : '', `Place of Supply: ${placeOfSupply(ownState)}`]);
  y = partyBlock(doc, y, 'Vendor:', po.vendorName, po.vendorAddress, po.vendorGSTIN);
  const contact = [po.vendorPhone ? `Ph: ${po.vendorPhone}` : '', po.vendorEmail ? `Email: ${po.vendorEmail}` : ''].filter(Boolean).join('   ');
  if (contact) { doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.text(contact, 14, y - 1); y += 4; }
  const first = (items[0] && items[0].description) ? String(items[0].description).split(/ - |\n/)[0].slice(0, 70) : 'goods';
  y = subjectLine(doc, y, po.subject || `Purchase Order for ${first}${items.length > 1 ? ` and ${items.length - 1} more item(s)` : ''}`);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`Please deliver to: ${company.name}, ${company.address}`, 14, y); y += 6;

  const half = t.rate / 2;
  const head = t.interstate
    ? [['#', 'Item & Description', 'HSN/SAC', 'Qty', 'Unit', 'Rate', 'IGST%', 'IGST Amt', 'Amount']]
    : [['#', 'Item & Description', 'HSN/SAC', 'Qty', 'Unit', 'Rate', 'CGST%', 'CGST Amt', 'SGST%', 'SGST Amt', 'Amount']];
  const rows = items.map((i, idx) => {
    const base = [idx + 1, i.description, i.hsn || '', i.qty, i.unit || 'Nos', (parseFloat(i.rate) || 0).toFixed(2)];
    return t.interstate
      ? [...base, `${t.rate}%`, (i.amount * t.rate / 100).toFixed(2), i.amount.toFixed(2)]
      : [...base, `${half}%`, (i.amount * half / 100).toFixed(2), `${half}%`, (i.amount * half / 100).toFixed(2), i.amount.toFixed(2)];
  });
  autoTable(doc, {
    startY: y, head, body: rows, theme: 'grid',
    headStyles: { fillColor: [37, 99, 235], fontSize: 8 }, styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 42 } }
  });

  let f = doc.lastAutoTable.finalY + 6;
  const tx = pageWidth - 70;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  const line = (label, val) => { doc.text(label, tx, f); doc.text(val, pageWidth - 14, f, { align: 'right' }); f += 5; };
  line('Sub Total', t.net.toFixed(2));
  if (t.interstate) line(`IGST (${t.rate}%)`, t.igst.toFixed(2));
  else { line(`CGST (${half}%)`, t.cgst.toFixed(2)); line(`SGST (${half}%)`, t.sgst.toFixed(2)); }
  f += 1; doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text('Total', tx, f); doc.text(`Rs. ${t.total.toFixed(2)}`, pageWidth - 14, f, { align: 'right' });
  f += 10;
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.text('Total In Words:', 14, f);
  doc.setFont('helvetica', 'italic'); doc.text(`Indian Rupee ${deps.numberToWordsINR(t.total)}`, 14, f + 5);
  f += 14;

  const flow = makeFlow(doc, f, pageHeight, pageWidth);
  flow.para('Notes', po.notes, 9);
  flow.para('Terms & Conditions', po.terms, 8);
  signature(doc, company, deps, flow.y(), pageHeight, pageWidth);
  return doc;
};
